import { GenericPad, KeyboardEventTypes, Vector3, Xbox360Button, Xbox360Pad } from '@babylonjs/core';
import { Button, Control, Grid, InputText, StackPanel } from '@babylonjs/gui';
import { Game } from './Game';
import { GameState } from './GameState';
import { AtlasLoader } from './gui/AtlasLoader';
import { Gui, MenuNavigation } from './gui/Gui';

export class MainMenu {
  private xboxAtlas: AtlasLoader;
  private keyboardAtlas: AtlasLoader;
  private touchAtlas: AtlasLoader;
  private nav: MenuNavigation;

  constructor(public game: Game) {}

  async create() {
    await Gui.createDefaultScene(this.game, {
      url: 'assets/skybox/skybox_ruins.glb',
      rotation: new Vector3(-Math.PI / 2, 0, 0),
      rotationAxis: 'y',
    });
    this.nav = new MenuNavigation(this.game);
    this.nav.addControl(await this.createPlayButton());
    this.nav.addControl(await this.createMultiplayerButton());
    this.nav.addControl(await this.createSettingsButton());
    this.nav.addControl(await this.createHighscoresButton());
    this.nav.addControl(await this.createIntroButton());
    this.nav.start();

    await this.createNameInput();
    await this.game.sound.createBackgroundMusic('IntroScreen');
    const mesh = await Gui.createAnimatedPlayer(this.game, 'Capoeira', new Vector3(6.5, -3, 0), true);
    mesh.scaling.scaleInPlace(3);

    this.game.sound.speak('Welcome to Platformia!', 2000);

    if (this.game.user.name === 'Player') {
      this.game.sound.speak('Please enter your name!', 5000);
    } else {
      this.game.sound.speak('Nice to see you again!', 5000);
    }

    this.xboxAtlas = new AtlasLoader(this.game.assetFactory, 'xbox-series_sheet_double', 'xbox-series_sheet_double.png', true, 1280, 1280);
    this.keyboardAtlas = new AtlasLoader(
      this.game.assetFactory,
      'keyboard_n_mouse_sheet_double',
      'keyboard_n_mouse_sheet_double.png',
      true,
      2048,
      2048
    );
    this.touchAtlas = new AtlasLoader(this.game.assetFactory, 'touch_sheet_double', 'touch_sheet_double.png', true, 768, 640);

    await this.xboxAtlas.load();
    await this.keyboardAtlas.load();
    await this.touchAtlas.load();

    this.createControlsGrid();
  }

  play() {
    this.game.sound.play('click');
    this.game.sound.speak("Let's go, " + this.game.user.name + '!');
    this.game.routeTo(GameState.SELECTION);
  }

  multiplayer() {
    this.game.sound.play('click');
    this.game.routeTo(GameState.LOBBY);
  }

  settings() {
    this.game.sound.play('click');
    this.game.routeTo(GameState.SETTINGS);
  }

  intro() {
    this.game.sound.play('click');
    this.game.routeTo(GameState.INTRO);
  }

  highscores() {
    this.game.sound.play('click');
    this.game.routeTo(GameState.HIGHSCORES);
  }

  async createPlayButton(): Promise<Button> {
    const button = this.game.gui2D.createButton(
      'button_play',
      '🎮 Single Player',
      () => {
        this.play();
      },
      this.game.gui2D.ui.rootContainer,
      1,
      this.game.gui2D.sceneButtonColors()
    );
    this.game.gui2D.styleSceneButton(button);
    button.top = '-5%';
    return button;
  }

  async createMultiplayerButton(): Promise<Button> {
    const button = this.game.gui2D.createButton(
      'button_multiplayer',
      '🎮🕹️ Two Players',
      () => {
        this.multiplayer();
      },
      this.game.gui2D.ui.rootContainer,
      1,
      this.game.gui2D.sceneButtonColors()
    );
    this.game.gui2D.styleSceneButton(button);
    button.top = '5%';
    return button;
  }

  async createSettingsButton(): Promise<Button> {
    const button = this.game.gui2D.createButton(
      'button_settings',
      '🎛️ Settings',
      () => {
        this.settings();
      },
      this.game.gui2D.ui.rootContainer,
      1,
      this.game.gui2D.sceneButtonColors()
    );
    this.game.gui2D.styleSceneButton(button);
    button.top = '15%';
    return button;
  }

  async createHighscoresButton(): Promise<Button> {
    const button = this.game.gui2D.createButton(
      'button_highscores',
      '🏆 Highscores',
      () => {
        this.highscores();
      },
      this.game.gui2D.ui.rootContainer,
      1,
      this.game.gui2D.sceneButtonColors()
    );
    this.game.gui2D.styleSceneButton(button);
    button.top = '25%';
    return button;
  }

  async createIntroButton(): Promise<Button> {
    const button = this.game.gui2D.createButton(
      'button_intro',
      '🎥 Intro',
      () => {
        this.intro();
      },
      this.game.gui2D.ui.rootContainer,
      1,
      this.game.gui2D.sceneButtonColors()
    );
    this.game.gui2D.styleSceneButton(button);
    button.top = '35%';
    return button;
  }

  async createNameInput(): Promise<InputText> {
    const input = this.game.gui2D.createTextField(
      'input_name',
      this.game.user.name,
      (text) => {
        this.game.user.name = text;
        this.game.user.save();
      },
      this.game.gui2D.ui.rootContainer
    );
    input.fontSize = Gui.fontSizeXL;
    input.width = '20%';
    input.height = Gui.fontSizeXXL;
    input.color = 'white';
    input.top = '-21%';
    input.thickness = 1;
    input.background = Gui.controlBackground;
    return input;
  }

  createControlsGrid() {
    const container = this.game.gui2D.createRectangle('20%', '60%', this.game.gui2D.ui.rootContainer);
    container.horizontalAlignment = Control.HORIZONTAL_ALIGNMENT_LEFT;
    container.verticalAlignment = Control.VERTICAL_ALIGNMENT_CENTER;
    container.left = '2%';
    container.thickness = 0;
    container.background = Gui.controlBackground;
    container.cornerRadius = 20;

    const title = this.game.gui2D.createTextBlock('controls_title', 'Controls', container, Gui.fontSizeL);
    title.verticalAlignment = Control.VERTICAL_ALIGNMENT_TOP;
    title.top = '10px';
    title.height = '20px';
    title.color = Gui.secondaryColor.toHexString();
    title.textHorizontalAlignment = Control.HORIZONTAL_ALIGNMENT_CENTER;

    const grid = new Grid('controls_grid');
    grid.width = '90%';
    grid.height = '80%';
    grid.addRowDefinition(0.2);
    grid.addRowDefinition(0.2);
    grid.addRowDefinition(0.2);
    grid.addRowDefinition(0.2);
    grid.addRowDefinition(0.2);
    grid.addRowDefinition(0.2);
    grid.addColumnDefinition(0.35);
    grid.addColumnDefinition(0.65);

    container.addControl(grid);
    container.top = '40px';

    // Actions
    const actions = [
      {
        name: 'Move',
        inputs: ['keyboard_w_outline', 'keyboard_a_outline', 'keyboard_s_outline', 'keyboard_d_outline', 'xbox_stick_l', 'touch_tap_hold'],
      },
      {
        name: 'Jump',
        inputs: ['keyboard_space_icon_outline', 'xbox_button_a', 'xbox_button_a_outline'],
      },
      {
        name: 'Dash/Airkick',
        inputs: ['keyboard_q_outline', 'xbox_button_b', 'xbox_button_b_outline'],
      },
      {
        name: 'Throw',
        inputs: ['keyboard_e_outline', 'xbox_button_x', 'xbox_button_x_outline'],
      },
      {
        name: 'Aim',
        inputs: ['keyboard_shift_icon_outline', 'xbox_lt', 'touch_tap'],
      },
      {
        name: 'Camera',
        inputs: ['mouse', 'xbox_stick_r', 'touch_tap'],
      },
    ];

    actions.forEach((action, index) => {
      // Label
      const label = this.game.gui2D.createTextBlock(`label_${index}`, action.name, undefined, Gui.fontSizeXXXL);
      label.textHorizontalAlignment = Control.HORIZONTAL_ALIGNMENT_LEFT;
      grid.addControl(label, index, 0);

      // Icons Panel
      const panel = new StackPanel();
      panel.isVertical = false;
      panel.horizontalAlignment = Control.HORIZONTAL_ALIGNMENT_LEFT;
      panel.verticalAlignment = Control.VERTICAL_ALIGNMENT_CENTER;
      panel.height = '100%';

      grid.addControl(panel, index, 1);

      action.inputs.forEach((inputName) => {
        let options: string | number = '36px'; // pixel size for icon
        let img = this.keyboardAtlas.createImage(inputName, options, options);
        if (!img) {
          img = this.xboxAtlas.createImage(inputName, options, options);
        }
        if (!img) {
          img = this.touchAtlas.createImage(inputName, options, options);
        }
        if (img) {
          img.paddingRight = '5px';
          panel.addControl(img);
        }
      });
    });
  }
}
